// REALISTIC ZOMBIE (2026-10-23, owner directive: "really realistic models",
// zombie look explicitly unfrozen — the PLAYER avatar look stays frozen, see
// CLAUDE.md invariant 5). A rigged, skinned human (Microsoft Rocketbox
// Male_Adult_01, MIT) repainted as a zombie, driven by six retargeted CC0
// clips (see public/models/zombie/CREDITS.md and agent/zombie-asset/).
//
// Avatar owns the lifecycle: while an avatar is a zombie AND this module is
// armed, it hides its procedural body and drives one ZombieModel instead.
//
// Contract:
// - ONE skinned mesh, two draw calls (skin+head atlas, alpha-tested hair) per
//   zombie; materials are SHARED (one pair per tint colour, module-cached,
//   never disposed) so a 30-zombie purge wave is 60 draws, not 30x the
//   procedural body's ~15 parts.
// - Never fades: the materials are tagged `npNoFade` so Avatar's opacity /
//   ghost passes skip them. A transparent skinned variant would be a fresh
//   shader program compiled mid-fight; the corpse is removed by the server
//   1.6 s after death anyway, before any fade would read.
// - Armed ONLY by warmZombieModel() under the match load screen, after one
//   real frame through the game's render path has compiled its programs
//   (colour + skinned shadow depth) and uploaded its textures. If the GLB is
//   not loaded by then, the round keeps the procedural zombie — it never pops
//   in mid-fight.
// - PER TYPE (zombieTypes.ts, 2026-11-04): each server variant (walker
//   outfits, runner, brute, ghoul, toxic, leaper, spitter, wraith, boss) gets
//   its own skin / clothes / blood / glow palette and its own proportions +
//   posture. ONE patched skin program serves every type (onBeforeCompile is a
//   single shared function, so three's program cache key is identical): the
//   types only differ in uniform values, bone offsets and root scale. The
//   region mask is uploaded by the same warm frame.
// - No per-frame allocation (invariant 6): update() writes weights/timeScales
//   on preallocated actions and calls mixer.update once.
import * as THREE from "three";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import { gltfWithin, instantiate, loadGltf, warmStageNow, type WarmStage } from "./gltfAssets.js";
import { applyRig, poseRig, zombieLookFor, type RigPose, type ZombieLook } from "./zombieTypes.js";

export const ZOMBIE_GLB_URL = "/models/zombie/zombie.glb";
/** Region mask for the per-type look (R skin, G blood, B shirt, A trousers /
 *  scalp hair) — built offline from the GLB's own atlas by
 *  agent/zombie-asset/mask.mjs; 1024x512, glTF UV orientation. */
export const ZOMBIE_MASK_URL = "/models/zombie/zombie_mask.webp";

/** The GLB's idle pose crowns at 1.808 m (hair cards included; measured in a
 *  real browser with Box3.setFromObject(precise) across every clip frame —
 *  agent/shot-zombie.mjs `measure`). Scaled so that crown lands at 1.70 m, the
 *  top of the server's 1.7 m hitbox: the visible head then spans ~1.43–1.70 m,
 *  inside the headshot band (top 25% = 1.275–1.70 m), like the procedural
 *  head (top 1.705). The model faces +Z; avatars face -Z, hence the π yaw. */
export const ZOMBIE_MODEL_SCALE = 1.70 / 1.808;

/** Clip order == action index. */
const CLIPS = ["idle", "walk", "run", "attack", "death", "hit"] as const;
const IDLE = 0, WALK = 1, RUN = 2, ATTACK = 3, DEATH = 4, HIT = 5;
const N_LOCO = 5; // idle..death are mutually exclusive; HIT is an overlay
/** Ground speed each in-place loop was authored for (m/s, after the scale) —
 *  measured from the planted foot's travel in the retargeted clips. */
const WALK_NOMINAL = 0.26;
const RUN_NOMINAL = 1.7;
/** Locomotion bands with hysteresis (m/s). Zombies move at 2.3–5.1 m/s
 *  (MOVE.walkSpeed 5.7 x zombie.speed 0.4–0.9), so RUN carries almost all
 *  movement; the slow lurch WALK covers stopping, turning and the stab slow. */
const WALK_ON = 0.35, WALK_OFF = 0.2, RUN_ON = 1.1, RUN_OFF = 0.85;
/** The server deletes a dead zombie 1.6 s after the kill — the 3.3 s death
 *  take is played fast enough to reach the ground inside that window. */
const DEATH_RATE = 1.75;
const HIT_LEN = 0.55;
const FAR_STEP = 1 / 15; // far-LOD mixer cadence

let template: GLTF | null = null;
let armed = false;
/** The renderer the warm frame ran on. Every match builds its own
 *  GameRenderer (= a fresh WebGL context with an empty program cache), so
 *  arming is per renderer: a new match re-warms before it re-arms. */
let armedFor: object | null = null;
let skinMat: THREE.MeshStandardMaterial | null = null;
let hairMat: THREE.MeshStandardMaterial | null = null;
const lookCache = new Map<string, [THREE.MeshStandardMaterial, THREE.MeshStandardMaterial]>();
let maskTex: THREE.Texture | null = null;
let maskLoad: Promise<THREE.Texture> | null = null;
/** Stand-in when the mask cannot load: all regions 0 = the baked look (same
 *  sampler, so still the same program). */
let maskNone: THREE.DataTexture | null = null;
function noMask(): THREE.DataTexture {
  if (!maskNone) {
    maskNone = new THREE.DataTexture(new Uint8Array(4), 1, 1);
    maskNone.needsUpdate = true;
    maskNone.userData.shared = true;
  }
  return maskNone;
}
function loadMask(): Promise<THREE.Texture> {
  if (!maskLoad) {
    // ImageBitmap with premultiply OFF: the mask is data, and a premultiplied
    // decode would zero the R/G/B regions wherever A (trousers) is 0
    const ld = new THREE.ImageBitmapLoader();
    ld.setOptions({ imageOrientation: "none", premultiplyAlpha: "none", colorSpaceConversion: "none" });
    maskLoad = ld.loadAsync(ZOMBIE_MASK_URL).then((bmp) => {
      const t = new THREE.Texture(bmp as unknown as HTMLImageElement);
      t.flipY = false; // glTF UV orientation, like the GLB's own textures
      t.colorSpace = THREE.NoColorSpace; // data, not colour
      t.userData.shared = true;
      t.needsUpdate = true;
      maskTex = t;
      return t;
    }).catch((err) => {
      console.error("[zombie] mask failed to load — baked look for every type:", err);
      maskTex = noMask();
      return maskTex;
    });
  }
  return maskLoad;
}

/** True once warmZombieModel() has compiled + uploaded everything. */
export function zombieModelArmed(): boolean { return armed && template !== null; }

/** A match that will NOT warm (weak device / non-zombie mode) must not inherit
 *  the previous match's arming: that was a different WebGL context, so the
 *  skinned programs would cold-compile mid-fight here. */
export function disarmZombieModel(): void { armed = false; armedFor = null; }

function prepare(g: GLTF | null): GLTF | null {
  if (!g || template) return template ?? g;
  g.scene.updateMatrixWorld(true);
  g.scene.traverse((o) => {
    const m = o as THREE.SkinnedMesh;
    if (!m.isSkinnedMesh) return;
    // Frustum culling for a skinned mesh tests `boundingSphere` in mesh space;
    // left null, three would compute it on first cull by skinning every
    // vertex (per instance). Compute it ONCE on the template at bind pose and
    // pad it for the reach of the attack and the death fall (~1.3 m of root
    // travel). Clones copy it.
    m.computeBoundingSphere();
    if (m.boundingSphere) m.boundingSphere.radius += 1.4;
    m.castShadow = true;
    m.receiveShadow = true;
    const mat = m.material as THREE.MeshStandardMaterial;
    mat.userData.npNoFade = true;
    if (mat.name === "zombie_hair") hairMat = mat; else skinMat = mat;
  });
  if (!skinMat || !hairMat) { console.error("[zombie] unexpected GLB layout"); return null; }
  template = g;
  return g;
}

/** Start the fetch early (match start) so the load screen only has to wait
 *  for whatever is left. Safe to call repeatedly. */
export function preloadZombieModel(): void {
  void loadGltf(ZOMBIE_GLB_URL).then(prepare);
  void loadMask();
}

// ---------------------------------------------------------------- per-type skin shader
// Appended after <map_fragment>: recolours the baked albedo by REGION (mask)
// instead of one flat multiply — dead skin, shirt, trousers, scalp hair and
// blood each take the type's colour at the baked luminance (so stripes, seams,
// veins and folds survive), then procedural UV-space noise adds the type's
// grime, torn-open cloth (with ribs showing for the gaunt ones), extra gore,
// char, and emissive pustules / ember cracks. All branch-free on uniforms.
const ZOMBIE_FRAG_HEAD = /* glsl */ `
uniform sampler2D zMask;
uniform vec3 zSkin, zShirt, zPants, zHair, zBlood, zGlow;
uniform vec4 zSkinP, zClothP, zBloodP;
uniform vec2 zGlowP;
float zh(vec2 p) { vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
float zn(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(zh(i), zh(i + vec2(1.0, 0.0)), f.x), mix(zh(i + vec2(0.0, 1.0)), zh(i + vec2(1.0, 1.0)), f.x), f.y); }
float zf(vec2 p) { return (zn(p) * 0.5 + zn(p * 2.03 + 1.7) * 0.25 + zn(p * 4.07 + 3.1) * 0.125) / 0.875; }
`;
const ZOMBIE_FRAG_BODY = /* glsl */ `
vec3 zE = vec3(0.0);
{
  vec4 zm = texture2D(zMask, vMapUv);
  vec3 c = diffuseColor.rgb;
  float L = dot(c, vec3(0.2126, 0.7152, 0.0722));
  vec2 q = vMapUv * vec2(2.0, 1.0); // square noise cells on the 2:1 atlas
  float nA = zf(q * 8.0), nB = zn(q * 40.0 + 3.7), nC = zf(q * 3.5 + 5.1);
  float headHalf = step(0.5, vMapUv.x);
  float sW = zm.r, bW = zm.g, tW = zm.b * (1.0 - headHalf);
  float pW = zm.a * (1.0 - headHalf), hW = zm.a * headHalf;
  float cW = tW + pW;
  // ---- dead skin: the type's tone at the baked luminance, blotched, charred
  float mott = mix(1.0, 0.55 + 0.9 * nA, zSkinP.y);
  vec3 sk0 = zSkin * mott;
  vec3 sk = zSkin * clamp(L / 0.16, 0.0, 2.0) * mott;
  float crack = 1.0 - smoothstep(0.0, 0.022, abs(nA - 0.5));
  float charK = zSkinP.z * (0.55 + 0.45 * smoothstep(0.35, 0.65, nC));
  vec3 charC = vec3(0.022, 0.018, 0.016) * (0.6 + 0.8 * nB);
  sk = mix(sk, charC, charK);
  c = mix(c, sk, sW * zSkinP.x);
  // ---- clothes (optional plaid), scalp hair
  float plaid = zBloodP.w * 0.4 * max(step(0.6, fract(q.x * 24.0)), step(0.6, fract(q.y * 24.0)));
  vec3 sh0 = zShirt * (1.0 - plaid);
  vec3 sh = sh0 * clamp(L / 0.17, 0.0, 1.8);
  vec3 pa = zPants * clamp(L / 0.11, 0.0, 1.8);
  c = mix(c, sh, tW * zClothP.x);
  c = mix(c, pa, pW * zClothP.y);
  c = mix(c, zHair * clamp(L / 0.016, 0.0, 2.5), hW);
  // scorched cloth follows the char
  c = mix(c, c * (0.25 + 0.5 * nB), cW * zSkinP.z * 0.8);
  // ---- grime
  c *= mix(1.0, 0.45 + 0.65 * nC, zClothP.z * cW);
  // ---- torn-open cloth: dark frayed rim, dead flesh (or ribs) beneath
  float tT = 0.8 - zClothP.w * 0.9;
  float tn = nC * 0.75 + nB * 0.25;
  float tear = cW * smoothstep(tT, tT + 0.02, tn);
  float rim = cW * smoothstep(tT - 0.05, tT, tn) * (1.0 - tear);
  float rib = smoothstep(0.45, 0.8, abs(fract(vMapUv.y * 60.0) * 2.0 - 1.0)) * tW * zBloodP.z;
  vec3 flesh = mix(sk0 * (0.7 + 0.5 * nB), vec3(0.05, 0.006, 0.005) + sk0 * 0.15, zBloodP.z * tW);
  flesh = mix(flesh, vec3(0.42, 0.38, 0.3) * (0.7 + 0.4 * nB), rib);
  flesh = mix(flesh, charC, charK);
  c = mix(c * (1.0 - 0.6 * rim), flesh, tear);
  // ---- blood: baked splats recoloured / washed out, plus extra gore
  float keep = zBloodP.x;
  vec3 bc = zBlood * (0.6 + 0.6 * nB);
  float regionK = min(1.0, sW + tW + pW);
  vec3 regionNom = (sW * sk0 + tW * sh0 + pW * zPants) / max(1e-3, sW + tW + pW);
  c = mix(c, regionNom * (0.75 + 0.35 * nB), clamp(1.0 - keep, 0.0, 1.0) * bW * regionK);
  c = mix(c, bc, clamp(bW * keep * 0.7, 0.0, 0.9));
  float gT = 0.86 - zBloodP.y * 0.55;
  float gore = smoothstep(gT, gT + 0.04, (1.0 - nC) * 0.7 + nB * 0.3) * (sW + cW) * step(0.001, zBloodP.y);
  c = mix(c, bc, gore * 0.85);
  // ---- glow: pustules (kind 0) or ember cracks in the char (kind 1)
  float pus = smoothstep(0.86, 0.93, zn(q * 26.0 + 1.3)) * sW * (1.0 - zGlowP.y);
  c = mix(c, c * 0.4, smoothstep(0.78, 0.86, zn(q * 26.0 + 1.3)) * sW * (1.0 - zGlowP.y) * step(0.001, zGlowP.x));
  zE = zGlow * zGlowP.x * (pus * (0.6 + 0.4 * nB) + zGlowP.y * crack * sW * charK * (0.4 + nB));
  diffuseColor.rgb = c;
}
`;
type ZUniforms = Record<string, THREE.IUniform>;
/** ONE function object shared by every type's material — three keys the
 *  program cache on onBeforeCompile.toString(), so all types share a program. */
function patchZombieSkin(this: THREE.Material, shader: THREE.WebGLProgramParametersWithUniforms): void {
  Object.assign(shader.uniforms, this.userData.zU as ZUniforms);
  shader.fragmentShader = shader.fragmentShader
    .replace("void main() {", ZOMBIE_FRAG_HEAD + "\nvoid main() {")
    .replace("#include <map_fragment>", "#include <map_fragment>\n" + ZOMBIE_FRAG_BODY)
    .replace("#include <emissivemap_fragment>", "#include <emissivemap_fragment>\ntotalEmissiveRadiance += zE;");
}
const v3 = (a: readonly number[]) => new THREE.Vector3(a[0], a[1], a[2]);
const v4 = (a: readonly number[]) => new THREE.Vector4(a[0], a[1], a[2], a[3]);
/** Baked hair-card albedo the hair tint is relative to (linear). */
const BAKED_HAIR = 0.023;

/** Shared material pair for one zombie TYPE (keyed by look id, module-cached,
 *  never disposed). Colours are uniforms — every type hits the same compiled
 *  program pair. */
function materialsFor(look: ZombieLook): [THREE.MeshStandardMaterial, THREE.MeshStandardMaterial] {
  let pair = lookCache.get(look.id);
  if (!pair) {
    const s = skinMat!.clone(), h = hairMat!.clone();
    s.userData.zU = {
      zMask: { value: maskTex ?? noMask() },
      zSkin: { value: v3(look.skin) }, zShirt: { value: v3(look.shirt) }, zPants: { value: v3(look.pants) },
      zHair: { value: v3(look.hair) }, zBlood: { value: v3(look.blood) }, zGlow: { value: v3(look.glow) },
      zSkinP: { value: v4(look.skinP) }, zClothP: { value: v4(look.clothP) }, zBloodP: { value: v4(look.bloodP) },
      zGlowP: { value: new THREE.Vector2(look.glowP[0], look.glowP[1]) },
    } satisfies ZUniforms;
    s.onBeforeCompile = patchZombieSkin;
    s.roughness = look.skinP[3]; // wet types sheen (multiplies the roughness map)
    h.color.setRGB(look.hair[0] / BAKED_HAIR, look.hair[1] / BAKED_HAIR, look.hair[2] / BAKED_HAIR);
    h.visible = look.hairCards; // hairless types skip the second draw
    for (const m of [s, h]) { m.userData.shared = true; m.userData.npNoFade = true; }
    pair = [s, h];
    lookCache.set(look.id, pair);
  }
  return pair;
}

export class ZombieModel {
  /** Container in avatar-group space: yaw π + scale. */
  readonly root = new THREE.Group();
  private readonly inner: THREE.Object3D;
  private readonly mixer: THREE.AnimationMixer;
  private readonly acts: THREE.AnimationAction[] = [];
  private readonly w = new Float32Array(N_LOCO);
  private readonly skinned: THREE.SkinnedMesh[] = [];
  private loco = IDLE;
  private hitT = -1;
  private dying = false;
  private farAcc = 0;
  /** Per-instance rate jitter so a horde doesn't step in lockstep. */
  private readonly jitter = 0.92 + Math.random() * 0.16;
  /** The type's posture layer (null = none), re-applied after each mixer step. */
  private readonly pose: RigPose | null;

  /** `tint` = the server bodyColor, which identifies the zombie type. */
  constructor(tint = "") {
    const g = template!;
    const look = zombieLookFor(tint);
    this.inner = instantiate(g);
    this.root.add(this.inner);
    this.root.rotation.y = Math.PI;
    const r = look.rig;
    // per-type bulk (X/Z) and the measured crown fit (Y) — see zombieTypes.ts
    this.root.scale.set(ZOMBIE_MODEL_SCALE * r.width, ZOMBIE_MODEL_SCALE * r.fit, ZOMBIE_MODEL_SCALE * r.width);
    this.root.position.y = r.lift * ZOMBIE_MODEL_SCALE * r.fit;
    this.root.name = "npZombieModel";
    this.pose = applyRig(this.inner, r);
    const [s, h] = materialsFor(look);
    this.inner.traverse((o) => {
      const m = o as THREE.SkinnedMesh;
      if (!m.isSkinnedMesh) return;
      m.material = (m.material as THREE.Material).name === "zombie_hair" ? h : s;
      this.skinned.push(m);
    });
    this.mixer = new THREE.AnimationMixer(this.inner);
    for (const name of CLIPS) {
      const clip = THREE.AnimationClip.findByName(g.animations, name);
      const a = this.mixer.clipAction(clip);
      if (name === "death") { a.setLoop(THREE.LoopOnce, 1); a.clampWhenFinished = true; a.timeScale = DEATH_RATE; }
      a.play();
      a.setEffectiveWeight(0);
      // desync: every zombie starts its loops at a different phase
      if (name !== "death" && name !== "hit") a.time = Math.random() * clip.duration;
      this.acts.push(a);
    }
    this.reset();
  }

  /** Back to a standing idle (spawn / revive). */
  reset(): void {
    this.dying = false;
    this.hitT = -1;
    this.loco = IDLE;
    this.w.fill(0);
    this.w[IDLE] = 1;
    this.acts[DEATH].reset();
    this.acts[DEATH].timeScale = DEATH_RATE;
    for (let i = 0; i < this.acts.length; i++) this.acts[i].setEffectiveWeight(i === IDLE ? 1 : 0);
    this.mixer.update(0);
    poseRig(this.pose);
  }

  startDeath(): void {
    if (this.dying) return;
    this.dying = true;
    this.hitT = -1;
    const d = this.acts[DEATH];
    d.reset();
    d.timeScale = DEATH_RATE;
  }

  /** Flinch overlay (a bullet landed). Ignored while dying. */
  hit(): void {
    if (this.dying || (this.hitT >= 0 && this.hitT < HIT_LEN * 0.6)) return;
    this.hitT = 0;
    this.acts[HIT].time = 0.08;
  }

  /** Once per rendered frame. `speed` = horizontal m/s. */
  update(dt: number, speed: number, attacking: boolean, far: boolean): void {
    if (far) {
      this.farAcc += dt;
      if (this.farAcc < FAR_STEP) return;
      dt = this.farAcc;
    }
    this.farAcc = 0;
    // ---- pick the locomotion layer (hysteretic speed bands)
    if (this.dying) this.loco = DEATH;
    else if (attacking) this.loco = ATTACK;
    else {
      const run = this.loco === RUN ? speed > RUN_OFF : speed > RUN_ON;
      const walk = this.loco === WALK || this.loco === RUN ? speed > WALK_OFF : speed > WALK_ON;
      this.loco = run ? RUN : walk ? WALK : IDLE;
    }
    const k = 1 - Math.exp(-dt * (this.dying ? 14 : 8));
    for (let i = 0; i < N_LOCO; i++) this.w[i] += ((i === this.loco ? 1 : 0) - this.w[i]) * k;
    let h = 0;
    if (this.hitT >= 0) {
      this.hitT += dt;
      if (this.hitT < HIT_LEN) h = 0.65 * Math.sin(Math.PI * this.hitT / HIT_LEN);
      else this.hitT = -1;
    }
    for (let i = 0; i < N_LOCO; i++) this.acts[i].setEffectiveWeight(this.w[i] * (1 - h));
    this.acts[HIT].setEffectiveWeight(h);
    // in-place loops: match the cadence to the real ground speed (some foot
    // slide at the extremes is the accepted cost of clamping)
    this.acts[WALK].timeScale = Math.min(2.4, Math.max(0.7, speed / WALK_NOMINAL)) * this.jitter;
    this.acts[RUN].timeScale = Math.min(2.3, Math.max(0.85, speed / RUN_NOMINAL)) * this.jitter;
    this.acts[ATTACK].timeScale = 1.15 * this.jitter;
    this.mixer.update(dt);
    poseRig(this.pose);
  }

  skinnedMeshes(): readonly THREE.SkinnedMesh[] { return this.skinned; }

  /** Free the per-instance pieces only: the skeleton's bone texture and the
   *  mixer's bindings. Geometry, textures and the tint materials are shared. */
  dispose(): void {
    this.mixer.stopAllAction();
    this.mixer.uncacheRoot(this.inner);
    for (const m of this.skinned) m.skeleton.dispose();
    this.root.removeFromParent();
  }
}

/** The minimal surface of GameRenderer this needs (kept structural so this
 *  module doesn't pull the renderer class in as a type cycle). */
interface WarmTarget { readonly scene: THREE.Scene; readonly camera: THREE.Camera; render(): void }

/**
 * Under the match load screen: wait (bounded) for the GLB, then put ONE
 * zombie in front of the camera and render ONE real frame through the game's
 * own path (post chain included) — that compiles the skinned colour program,
 * the skinned shadow-depth program (renderer.compile() never builds shadow
 * programs), and uploads the textures + bone texture. Then arm the module.
 * Idempotent; returns whether realistic zombies are live this round.
 * The CALLER gates weak devices (match.ts: lowSpec(), which includes the TV
 * app) — never calling this keeps the procedural zombie. (No renderer.js
 * import here: the esbuild avatar benches stub that module.)
 */
export async function warmZombieModel(r: WarmTarget, timeoutMs = 3000): Promise<boolean> {
  return warmStageNow(r, await stageZombieWarm(r, timeoutMs));
}

/** Batched form (gltfAssets WarmStage): precompile() compiles + draws the
 *  staged zombie together with the other models, then `arm()` + `dispose()`. */
export async function stageZombieWarm(r: WarmTarget, timeoutMs = 3000): Promise<WarmStage | null> {
  if (armed && armedFor === r) return { root: null, arm() { /* already warm */ }, dispose() { /* nothing staged */ } };
  armed = false; // a new context: nothing is compiled there yet
  armedFor = null;
  // the per-type region mask loads alongside the GLB (zombieTypes)
  const g = await gltfWithin(Promise.all([loadGltf(ZOMBIE_GLB_URL).then(prepare), loadMask()]).then(([gl]) => gl), timeoutMs);
  if (!g || !template) { console.log("[zombie] model not ready at the load-screen deadline — procedural zombies this round"); return null; }
  // the baseline look: its draw compiles the shared per-type skin program and
  // uploads the region mask every other type reuses
  const z = new ZombieModel("");
  const cam = r.camera;
  const fwd = new THREE.Vector3();
  cam.getWorldDirection(fwd);
  z.root.position.copy(cam.position).addScaledVector(fwd, 3);
  z.root.position.y -= 1.2;
  for (const m of z.skinnedMeshes()) m.frustumCulled = false; // also bypasses the shadow-camera cull
  z.update(1 / 60, 0, false, false);
  return { root: z.root, arm: () => { armed = true; armedFor = r; }, dispose: () => z.dispose() };
}
